<template>
  <div class="engraving-mask-editor">
    <v-row justify="center" align="center" class="editor-row">
      <EngravingMaskSideControls
        :rotation="params1.rotation"
        @zoom-in="zoomIn(1)"
        @zoom-out="zoomOut(1)"
        @update:rotation="v => { params1.rotation = v; redraw() }"
      />
      <EngravingMaskSideControls
        v-if="numParts === 3"
        :rotation="params2.rotation"
        @zoom-in="zoomIn(2)"
        @zoom-out="zoomOut(2)"
        @update:rotation="v => { params2.rotation = v; redraw() }"
      />
      <div class="engraving-mask-editor__canvas-wrap">
        <ShapeEditorCanvas
          ref="editorCanvas"
          :shape="shape"
          :size="size"
          :editor-size="editorSize"
          :num-parts="numParts"
          :is-horizontal="isHorizontal"
          :image1-loaded="image1Loaded"
          :image2-loaded="image2Loaded"
          :image3-loaded="image3Loaded"
          :drag-half="dragHalf"
          :disable-file-upload="disableFileUpload"
          @file-changed="onFileChanged"
          @drag-start="onDragStart"
          @upload-click="onUploadClick"
          @part-clicked="$emit('part-clicked', $event)"
        />
        <div v-if="anyPartLoading" class="engraving-mask-editor__loading">
          <div class="engraving-mask-editor__loading-content">
            <v-progress-circular
              :value="loadingProgressDisplay != null ? loadingProgressDisplay : undefined"
              :indeterminate="loadingProgressDisplay == null"
              color="primary"
              size="56"
              width="3"
              rotate="-90"
            />
            <span v-if="loadingProgressDisplay != null" class="engraving-mask-editor__loading-pct">
              {{ loadingProgressDisplay }}%
            </span>
            <span v-else class="engraving-mask-editor__loading-pct">Loading…</span>
          </div>
        </div>
      </div>
      <EngravingMaskSideControls
        v-if="numParts >= 2"
        :rotation="rightPanelRotation"
        @zoom-in="zoomIn(rightPanelPart)"
        @zoom-out="zoomOut(rightPanelPart)"
        @update:rotation="v => { rightPanelRotation = v }"
      />
    </v-row>
  </div>
</template>

<script>
import EngravingMaskSideControls from './EngravingMaskSideControls.vue'
import ShapeEditorCanvas from './ShapeEditorCanvas.vue'
import { renderEngravingMask } from './engravingMaskRenderer'
import {
  DEFAULT_OUTPUT_SIZE,
  EDITOR_DISPLAY_SIZE,
  ZOOM_STEP,
  ZOOM_MIN,
  ZOOM_MAX,
} from './constants'

const defaultParams = () => ({ x: 0, y: 0, rotation: 0, zoom: 1 })

export default {
  name: 'EngravingMaskEditor',
  components: { EngravingMaskSideControls, ShapeEditorCanvas },
  props: {
    numParts: { type: Number, default: 2, validator: (v) => v === 1 || v === 2 || v === 3 },
    direction: { type: String, default: 'horizontal', validator: (v) => v === 'vertical' || v === 'horizontal' },
    shape: { type: String, default: 'circle', validator: (v) => ['circle', 'heart', 'oval', 'rectangle-wide', 'rectangle-square'].includes(v) },
    outputSize: { type: Number, default: DEFAULT_OUTPUT_SIZE },
    spacing: { type: Number, default: 0 },
    /** When true, do not open file picker; images come from gallery (e.g. step 3 of EngravingGenerator). */
    disableFileUpload: { type: Boolean, default: false },
    /** Optional prefix for download filename (e.g. order number). Result: `${prefix}-${shape}-mask.png`. */
    downloadFilenamePrefix: { type: String, default: '' },
  },
  data() {
    return {
      size: DEFAULT_OUTPUT_SIZE,
      editorSize: EDITOR_DISPLAY_SIZE,
      image1: null,
      image2: null,
      image3: null,
      image1Loaded: false,
      image2Loaded: false,
      image3Loaded: false,
      image1Loading: false,
      image2Loading: false,
      image3Loading: false,
      image1LoadingProgress: null,
      image2LoadingProgress: null,
      image3LoadingProgress: null,
      params1: defaultParams(),
      params2: defaultParams(),
      params3: defaultParams(),
      dragHalf: null,
      dragStartX: 0,
      dragStartY: 0,
      dragStartParamX: 0,
      dragStartParamY: 0,
      didDrag: false,
    }
  },
  computed: {
    canDownload() {
      if (this.numParts === 1) return this.image1Loaded
      if (this.numParts === 2) return this.image1Loaded && this.image2Loaded
      return this.image1Loaded && this.image2Loaded && this.image3Loaded
    },
    anyPartLoading() {
      if (this.numParts === 1) return this.image1Loading
      if (this.numParts === 2) return this.image1Loading || this.image2Loading
      return this.image1Loading || this.image2Loading || this.image3Loading
    },
    /** Progress 0–100 for the currently loading part, or null for indeterminate (e.g. blob URL). */
    loadingProgressDisplay() {
      if (this.image1Loading) return this.image1LoadingProgress
      if (this.image2Loading) return this.image2LoadingProgress
      if (this.image3Loading) return this.image3LoadingProgress
      return null
    },
    showThreeParts() {
      return this.numParts === 3
    },
    isHorizontal() {
      return this.direction === 'vertical'
    },
    rightPanelPart() {
      return this.numParts === 3 ? 3 : 2
    },
    rightPanelRotation: {
      get() {
        return this.showThreeParts ? this.params3.rotation : this.params2.rotation
      },
      set(v) {
        if (this.showThreeParts) this.params3.rotation = v
        else this.params2.rotation = v
        this.redraw()
      },
    },
  },
  watch: {
    outputSize: {
      immediate: true,
      handler(v) {
        this.size = Math.max(256, Math.min(2048, v || DEFAULT_OUTPUT_SIZE))
        this.$nextTick(() => this.redraw())
      },
    },
    spacing() { this.redraw() },
    shape() { this.redraw() },
    numParts() { this.redraw() },
    direction() { this.redraw() },
    canDownload(val) {
      this.$emit('can-download-change', val)
    },
  },
  mounted() {
    this.$emit('can-download-change', this.canDownload)
  },
  methods: {
    onRotationInput() {
      this.redraw()
    },
    onFileChanged({ which, image }) {
      this['image' + which] = image
      this['image' + which + 'Loaded'] = true
      this.redraw()
    },
    /** Set image for a part from URL (e.g. from generator step 2 edits). Returns a Promise. */
    setImageFromUrl(which, url) {
      if (!url || which < 1 || which > 3) return Promise.resolve()
      this['image' + which + 'Loading'] = true
      this['image' + which + 'LoadingProgress'] = null
      const clearLoading = () => {
        this['image' + which + 'Loading'] = false
        this['image' + which + 'LoadingProgress'] = null
      }
      const setProgress = (p) => {
        this['image' + which + 'LoadingProgress'] = p
      }
      const isBlobOrData = typeof url === 'string' && (url.startsWith('blob:') || url.startsWith('data:'))
      if (isBlobOrData) {
        return new Promise((resolve, reject) => {
          const img = new Image()
          img.onload = () => {
            this['image' + which] = img
            this['image' + which + 'Loaded'] = true
            clearLoading()
            this.redraw()
            resolve()
          }
          img.onerror = () => {
            clearLoading()
            reject(new Error('Image load failed'))
          }
          img.src = url
        })
      }
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.open('GET', url)
        xhr.responseType = 'blob'
        xhr.withCredentials = false
        xhr.onprogress = (e) => {
          if (e.lengthComputable && e.total > 0) {
            setProgress(Math.min(99, Math.round((e.loaded / e.total) * 100)))
          }
        }
        xhr.onload = () => {
          if (xhr.status < 200 || xhr.status >= 300) {
            clearLoading()
            reject(new Error('Image load failed'))
            return
          }
          setProgress(100)
          const blob = xhr.response
          const objectUrl = URL.createObjectURL(blob)
          const img = new Image()
          img.onload = () => {
            this['image' + which] = img
            this['image' + which + 'Loaded'] = true
            clearLoading()
            this.redraw()
            URL.revokeObjectURL(objectUrl)
            resolve()
          }
          img.onerror = () => {
            clearLoading()
            URL.revokeObjectURL(objectUrl)
            reject(new Error('Image load failed'))
          }
          img.src = objectUrl
        }
        xhr.onerror = () => {
          clearLoading()
          reject(new Error('Image load failed'))
        }
        xhr.send()
      })
    },
    /** Clear one or all parts (which: 1, 2, 3, or null for all). */
    clearImage(which) {
      if (which != null) {
        this['image' + which] = null
        this['image' + which + 'Loaded'] = false
        this['image' + which + 'Loading'] = false
        this['image' + which + 'LoadingProgress'] = null
        if (which === 1) this.params1 = defaultParams()
        if (which === 2) this.params2 = defaultParams()
        if (which === 3) this.params3 = defaultParams()
      } else {
        this.image1 = this.image2 = this.image3 = null
        this.image1Loaded = this.image2Loaded = this.image3Loaded = false
        this.image1Loading = this.image2Loading = this.image3Loading = false
        this.image1LoadingProgress = this.image2LoadingProgress = this.image3LoadingProgress = null
        this.params1 = defaultParams()
        this.params2 = defaultParams()
        this.params3 = defaultParams()
      }
      this.redraw()
    },
    onUploadClick(which) {
      if (this.disableFileUpload) return
      if (this.didDrag) return
      if (which === 1 && !this.image1Loaded) this.$refs.editorCanvas.openFile(1)
      if (which === 2 && !this.image2Loaded) this.$refs.editorCanvas.openFile(2)
      if (which === 3 && !this.image3Loaded) this.$refs.editorCanvas.openFile(3)
    },
    onDragStart(which, e) {
      if (which === 1 && !this.image1Loaded) return
      if (which === 2 && !this.image2Loaded) return
      if (which === 3 && !this.image3Loaded) return
      this.dragHalf = which
      this.didDrag = false
      this.dragStartX = e.clientX
      this.dragStartY = e.clientY
      const params = which === 1 ? this.params1 : which === 2 ? this.params2 : this.params3
      this.dragStartParamX = params.x
      this.dragStartParamY = params.y
      document.addEventListener('mousemove', this.onDocMouseMove)
      document.addEventListener('mouseup', this.onDocMouseUp)
      document.addEventListener('touchmove', this.onDocTouchMove, { passive: false })
      document.addEventListener('touchend', this.onDocTouchEnd)
      document.addEventListener('touchcancel', this.onDocTouchEnd)
    },
    onDocMouseMove(e) {
      if (this.dragHalf == null) return
      this.didDrag = true
      const dx = e.clientX - this.dragStartX
      const dy = e.clientY - this.dragStartY
      const scale = this.size / this.editorSize
      const params = this.dragHalf === 1 ? this.params1 : this.dragHalf === 2 ? this.params2 : this.params3
      params.x = Math.round(this.dragStartParamX + dx * scale)
      params.y = Math.round(this.dragStartParamY + dy * scale)
      this.redraw()
    },
    onDocMouseUp() {
      document.removeEventListener('mousemove', this.onDocMouseMove)
      document.removeEventListener('mouseup', this.onDocMouseUp)
      document.removeEventListener('touchmove', this.onDocTouchMove)
      document.removeEventListener('touchend', this.onDocTouchEnd)
      document.removeEventListener('touchcancel', this.onDocTouchEnd)
      this.dragHalf = null
      setTimeout(() => { this.didDrag = false }, 0)
    },
    onDocTouchMove(e) {
      const t = e.touches && e.touches[0]
      if (t) this.onDocMouseMove({ clientX: t.clientX, clientY: t.clientY })
    },
    onDocTouchEnd() {
      this.onDocMouseUp()
    },
    zoomIn(which) {
      const p = which === 1 ? this.params1 : which === 2 ? this.params2 : this.params3
      p.zoom = Math.min(ZOOM_MAX, p.zoom + ZOOM_STEP)
      this.redraw()
    },
    zoomOut(which) {
      const p = which === 1 ? this.params1 : which === 2 ? this.params2 : this.params3
      p.zoom = Math.max(ZOOM_MIN, p.zoom - ZOOM_STEP)
      this.redraw()
    },
    redraw() {
      const canvas = this.$refs.editorCanvas && this.$refs.editorCanvas.$refs.canvas
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      renderEngravingMask(ctx, {
        size: this.size,
        shape: this.shape,
        numParts: this.numParts,
        direction: this.direction,
        spacing: this.spacing,
        images: [this.image1, this.image2, this.image3],
        params: [this.params1, this.params2, this.params3],
      })
    },
    download() {
      if (!this.canDownload) return
      const size = this.size
      const off = document.createElement('canvas')
      off.width = size
      off.height = size
      const ctx = off.getContext('2d')
      if (!ctx) return
      renderEngravingMask(ctx, {
        size,
        shape: this.shape,
        numParts: this.numParts,
        direction: this.direction,
        spacing: this.spacing,
        images: [this.image1, this.image2, this.image3],
        params: [this.params1, this.params2, this.params3],
        includeStroke: false,
      })
      try {
        const link = document.createElement('a')
        const prefix = String(this.downloadFilenamePrefix ?? '').trim()
        link.download = prefix ? `${prefix}-${this.shape}-mask.png` : `${this.shape}-mask.png`
        link.href = off.toDataURL('image/png')
        link.click()
      } catch (e) {
        console.warn('Download failed (e.g. CORS):', e)
      }
    },
  },
}
</script>

<style scoped>
.engraving-mask-editor {
  width: 100%;
  padding: 16px 0;
}

.editor-row {
  min-height: 320px;
}

.engraving-mask-editor__canvas-wrap {
  position: relative;
  display: inline-block;
}

.engraving-mask-editor__loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 8px;
  z-index: 2;
}

.engraving-mask-editor__loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.engraving-mask-editor__loading-pct {
  font-size: 0.875rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
}
</style>
